<template>
  <div class="project-finder-page">
    <!-- Top Filter Bar -->
    <div class="project-finder-page__filters">
      <div class="project-finder-page__filters-row">
        <!-- Project Filter -->
        <div class="project-finder-page__filter-item">
          <label class="project-finder-page__filter-label">Project</label>
          <themed-select
            v-model="selectedProjectId"
            :options="projectOptions"
            placeholder="Select Project..."
            :loading="loadingProjects"
            @input="onProjectSelected"
          />
        </div>

        <!-- Location Search -->
        <div class="project-finder-page__filter-item project-finder-page__filter-item--location">
          <label class="project-finder-page__filter-label">Search Location</label>
          <location-search
            @location-selected="onLocationSelected"
          />
        </div>

        <!-- City Filter -->
        <div class="project-finder-page__filter-item">
          <label class="project-finder-page__filter-label">City</label>
          <input
            v-model="filters.city"
            type="text"
            class="project-finder-page__filter-input"
            placeholder="City"
            @input="onFilterChange"
          />
        </div>

        <!-- State Filter -->
        <div class="project-finder-page__filter-item">
          <label class="project-finder-page__filter-label">State</label>
          <themed-select
            v-model="filters.state"
            :options="stateOptions"
            placeholder="State"
            @input="onFilterChange"
          />
        </div>

        <!-- Zip Filter -->
        <div class="project-finder-page__filter-item">
          <label class="project-finder-page__filter-label">Zip</label>
          <input
            v-model="filters.zip"
            type="text"
            class="project-finder-page__filter-input"
            placeholder="Zip"
            @input="onFilterChange"
          />
        </div>

        <!-- Property Type Filter -->
        <div class="project-finder-page__filter-item">
          <label class="project-finder-page__filter-label">Property Type</label>
          <themed-select
            v-model="filters.building_type_id"
            :options="buildingTypeOptions"
            placeholder="Type"
            @input="onFilterChange"
          />
        </div>

        <!-- Min SF Filter -->
        <div class="project-finder-page__filter-item">
          <label class="project-finder-page__filter-label">Min SF</label>
          <input
            v-model="filters.min_sf"
            type="number"
            class="project-finder-page__filter-input"
            placeholder="Min SF"
            @input="onFilterChange"
          />
        </div>

        <!-- Max SF Filter -->
        <div class="project-finder-page__filter-item">
          <label class="project-finder-page__filter-label">Max SF</label>
          <input
            v-model="filters.max_sf"
            type="number"
            class="project-finder-page__filter-input"
            placeholder="Max SF"
            @input="onFilterChange"
          />
        </div>

        <!-- SF Percentage Filter -->
        <div class="project-finder-page__filter-item">
          <label class="project-finder-page__filter-label">+/- SF %</label>
          <input
            v-model="filters.sf_percentage"
            type="number"
            class="project-finder-page__filter-input"
            placeholder="10"
            @input="onFilterChange"
          />
        </div>

        <!-- Reset Filters -->
        <div class="project-finder-page__filter-item project-finder-page__filter-item--reset">
          <label class="project-finder-page__filter-label">&nbsp;</label>
          <themed-button
            variant="outline"
            size="sm"
            @click="resetFilters"
            title="Reset all filters"
          >
            Reset
          </themed-button>
        </div>

        <!-- Search Button -->
        <!-- <div class="project-finder-page__filter-item project-finder-page__filter-item--actions">
          <button
            @click="searchProperties"
            class="project-finder-page__btn project-finder-page__btn--primary"
            :disabled="loadingProperties"
          >
            <theme-icon icon="search" type="icons" classes="w-4 h-4" />
            Search Properties
          </button>
          <button
            @click="resetFilters"
            class="project-finder-page__btn project-finder-page__btn--secondary"
          >
            Reset Filters
          </button>
        </div> -->
      </div>
    </div>

    <!-- Main Content: Map + Properties -->
    <div class="project-finder-page__content">
      <!-- Left: Map -->
      <div class="project-finder-page__map-container">
        <div id="project-finder-map" ref="mapContainer" class="project-finder-page__map"></div>
      </div>

      <!-- Right: Property Cards -->
      <div class="project-finder-page__sidebar">
        <div class="project-finder-page__sidebar-header">
          <h2 class="project-finder-page__sidebar-title">
            Properties ({{ properties.length }})
          </h2>
        </div>

        <div class="project-finder-page__grid-container">
          <themed-grid
            :data="properties"
            :fields="gridFields"
            :actions="gridActions"
            :columns="1"
            :per-page="50"
            :pagination="false"
            :loading="loadingProperties"
            item-key="id"
            :empty-message="selectedProjectId ? 'No properties found' : 'Select a project or search for properties'"
            empty-icon="search"
            @card-click="focusProperty"
            @action="handleGridAction"
            @disabled-action-click="handleDisabledActionClick"
          />
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div 
      v-if="loadingProperties" 
      class="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center z-40 pointer-events-none"
    >
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
        <p class="mt-3 text-sm font-medium text-gray-700 dark:text-gray-300">Loading properties...</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import ThemedSelect from '../ThemedSelect.vue';
import ThemedGrid from '../ThemedGrid.vue';
import LocationSearch from '../cresiteiq2/LocationSearch.vue';

// Import dark map styles from CRESiteIQ2
const DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }]
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }]
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#263c3f' }]
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6b9a76' }]
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#38414e' }]
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#212a37' }]
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9ca5b3' }]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#746855' }]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#1f2835' }]
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#f3d19c' }]
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#2f3948' }]
  },
  {
    featureType: 'transit.station',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#17263c' }]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#515c6d' }]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#17263c' }]
  }
];

// US States list
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export default {
  name: 'ProjectFinderPage',

  components: {
    ThemedSelect,
    ThemedGrid,
    LocationSearch
  },

  data() {
    return {
      // Google Maps instance
      map: null,
      
      // Theme state
      isDarkMode: false,
      
      // Project data
      projects: [],
      selectedProjectId: null,
      loadingProjects: false,
      projectCompIds: [], // Track building IDs already in project
      
      // Property data
      properties: [],
      loadingProperties: false,
      
      // Building types
      buildingTypes: [],
      
      // Location search
      selectedLocation: null,
      searchRadius: 10, // miles
      
      // Search filters
      filters: {
        city: '',
        state: '',
        zip: '',
        building_type_id: null,
        min_sf: '',
        max_sf: '',
        sf_percentage: 10
      },
      
      // Map markers
      markers: [],
      infoWindows: [],
      
      // UI state
      error: null,
      
      // Request cancellation
      currentRequestController: null
    };
  },

  computed: {
    /**
     * Format projects for ThemedSelect
     */
    projectOptions() {
      return this.projects.map(project => ({
        value: project.id,
        label: project.name
      }));
    },

    /**
     * Format states for ThemedSelect
     */
    stateOptions() {
      return US_STATES.map(state => ({
        value: state,
        label: state
      }));
    },

    /**
     * Format building types for ThemedSelect
     */
    buildingTypeOptions() {
      return this.buildingTypes.map(type => ({
        value: type.id,
        label: type.type
      }));
    },

    /**
     * Grid fields configuration for ThemedGrid
     */
    gridFields() {
      return [
        // Image
        {
          field: 'image_url',
          type: 'image',
          defaultImage: '/img/temp/building-sample.jpg'
        },
        // Title
        {
          field: 'name',
          type: 'title'
        },
        // Subtitle
        {
          field: 'location',
          type: 'subtitle'
        },
        // Display fields
        {
          field: 'available_sf',
          label: 'AVAILABLE SQ. FT.',
          type: 'number'
        },
        {
          field: 'total_sf',
          label: 'TOTAL SQ. FT.',
          type: 'number'
        },
        {
          field: 'type',
          label: 'TYPE',
          type: 'text',
          transform: (value) => value || '-'
        }
      ];
    },

    /**
     * Grid actions configuration for ThemedGrid
     */
    gridActions() {
      return [
        {
          name: 'request',
          label: 'Request Details',
          icon: 'download',
          variant: 'default',
          visible: (item) => {
            // Show for imported buildings that:
            // 1. Haven't been scraped yet (no status)
            // 2. Failed scraping (allow retry)
            // 3. Haven't been converted to a building yet
            const shouldShow = item.is_imported && 
                   (!item.scraper_status || item.scraper_status === 'failed') && 
                   !item.building_id;
            
            if (item.is_imported) {
              console.log('[ProjectFinder] Request Details visibility:', {
                id: item.id,
                scraper_status: item.scraper_status,
                building_id: item.building_id,
                shouldShow
              });
            }
            
            return shouldShow;
          },
          disabled: (item) => item.loading || this.loadingProperties
        },
        {
          name: 'status',
          label: 'Check Status',
          icon: 'refresh',
          variant: 'outline',
          visible: (item) => {
            // Show for imported buildings that are queued or processing (not failed, not done)
            const shouldShow = item.is_imported && 
                   item.scraper_status && 
                   item.scraper_status !== 'done' && 
                   item.scraper_status !== 'failed' && 
                   !item.building_id;
            
            if (item.is_imported) {
              console.log('[ProjectFinder] Check Status visibility:', {
                id: item.id,
                scraper_status: item.scraper_status,
                building_id: item.building_id,
                shouldShow
              });
            }
            
            return shouldShow;
          },
          disabled: (item) => item.loading || this.loadingProperties
        },
        {
          name: 'add',
          label: 'Add to Project',
          icon: 'add',
          variant: 'default',
          visible: (item) => {
            // Show for regular buildings not in project
            if (!item.is_imported) {
              return !this.isInProject(item.id);
            }
            // Show for imported buildings that have been converted to regular buildings
            if (item.is_imported && item.building_id) {
              return !this.isInProject(item.building_id);
            }
            return false;
          },
          disabled: (item) => {
            // Disable if no project selected or if loading
            return !this.selectedProjectId || item.loading || this.loadingProperties;
          },
          tooltip: (item) => {
            // Show tooltip when disabled due to no project selection
            if (!this.selectedProjectId) {
              return 'Select a project first to add properties';
            }
            return null;
          }
        },
        {
          name: 'remove',
          label: 'Remove',
          icon: 'trash',
          variant: 'destructive',
          visible: (item) => {
            // Always show for regular buildings in project (even if no project selected now)
            if (!item.is_imported) {
              return this.isInProject(item.id);
            }
            // Show for imported buildings that have been converted and are in project
            if (item.is_imported && item.building_id) {
              return this.isInProject(item.building_id);
            }
            return false;
          },
          disabled: (item) => {
            // Disable if no project selected or if loading
            return !this.selectedProjectId || item.loading || this.loadingProperties;
          },
          tooltip: (item) => {
            // Show tooltip when disabled due to no project selection
            if (!this.selectedProjectId) {
              return 'Select a project first to remove properties';
            }
            return null;
          }
        }
      ];
    }
  },

  mounted() {
    this.detectDarkMode();
    this.observeThemeChanges();
    this.waitForGoogleMaps();
    this.loadProjects();
    this.loadBuildingTypes();
  },

  beforeDestroy() {
    this.cancelPendingRequests();
    this.clearMarkers();
    
    // Disconnect theme observer
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
    
    // Clear the Google Maps check interval
    if (this.googleMapsCheckInterval) {
      clearInterval(this.googleMapsCheckInterval);
    }
  },

  methods: {
    /**
     * Wait for Google Maps API to load
     */
    waitForGoogleMaps() {
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps) {
        this.initializeMap();
        return;
      }

      // Wait for Google Maps to load (check every 100ms, max 10 seconds)
      let attempts = 0;
      const maxAttempts = 100; // 10 seconds total
      
      this.googleMapsCheckInterval = setInterval(() => {
        attempts++;
        
        if (window.google && window.google.maps) {
          clearInterval(this.googleMapsCheckInterval);
          console.log('[ProjectFinder] Google Maps API loaded successfully');
          this.initializeMap();
        } else if (attempts >= maxAttempts) {
          clearInterval(this.googleMapsCheckInterval);
          console.error('[ProjectFinder] Google Maps API failed to load after 10 seconds');
          
          if (this.$toast) {
            this.$toast.error('Failed to load Google Maps. Please refresh the page.');
          }
        }
      }, 100);
    },

    /**
     * Initialize Google Maps
     */
    initializeMap() {
      if (!window.google || !window.google.maps) {
        console.error('Google Maps API not loaded');
        return;
      }

      this.map = new google.maps.Map(this.$refs.mapContainer, {
        center: { lat: 39.8283, lng: -98.5795 }, // Center of USA
        zoom: 6,
        styles: this.isDarkMode ? DARK_MAP_STYLES : [],
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false
      });
    },

    /**
     * Handle location selection from LocationSearch component
     */
    onLocationSelected(location) {
      console.log('[ProjectFinder] Location selected:', location);

      this.selectedLocation = {
        name: location.name || location.address,
        lat: location.lat,
        lng: location.lng
      };

      // Search for properties near this location
      this.searchPropertiesByLocation();
    },

    /**
     * Search properties near selected location
     */
    async searchPropertiesByLocation() {
      if (!this.selectedLocation) {
        return;
      }

      // Cancel any pending requests
      this.cancelPendingRequests();

      // Create new AbortController for this request
      this.currentRequestController = new AbortController();

      this.loadingProperties = true;
      this.error = null;

      try {
        // Use the same /api/buildings/search endpoint with latitude/longitude parameters
        const response = await axios.get('/api/buildings/search', {
          params: {
            latitude: this.selectedLocation.lat,
            longitude: this.selectedLocation.lng,
            radius: this.searchRadius,
            ...this.filters
          },
          signal: this.currentRequestController.signal
        });

        console.log('[ProjectFinder] Location search response:', response.data);

        // Map the building data to properties format
        this.properties = response.data.map(building => {
          const property = {
            id: building.id,
            name: building.address_line_1 || building.name || 'Unknown Building',
            address: building.address_line_1 || building.name,
            city: building.city,
            state: building.state,
            location: `${building.city || ''}${building.city && building.state ? ', ' : ''}${building.state || ''}`,
            latitude: building.latitude,
            longitude: building.longitude,
            available_sf: building.available_square_footage,
            total_sf: building.total_square_footage,
            type: building.type?.type,
            image_url: building.image_url,
            is_added: this.isInProject(building.id),
            is_imported: building.is_imported || false,
            scraper_status: building.scraper_status,
            scraper_job_id: building.scraper_job_id,
            building_id: building.building_id,
            link: building.link,
            loading: false,
            building: building
          };
          
          return property;
        });

        // Center map on selected location and update markers
        this.map.setCenter({
          lat: this.selectedLocation.lat,
          lng: this.selectedLocation.lng
        });
        this.map.setZoom(12);

        this.updateMapMarkers();

        if (this.properties.length === 0) {
          this.$toast.info(`No properties found within ${this.searchRadius} miles of this location`);
        } else {
          this.$toast.success(`Found ${this.properties.length} properties near this location`);
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Request canceled:', error.message);
        } else {
          console.error('Error searching properties by location:', error);
          this.error = 'Failed to search properties by location';
          this.$toast.error('Failed to search properties by location');
        }
      } finally {
        this.loadingProperties = false;
        this.currentRequestController = null;
      }
    },

    /**
     * Detect if dark mode is active
     */
    detectDarkMode() {
      const htmlElement = document.documentElement;
      const bodyElement = document.body;
      
      this.isDarkMode = htmlElement.classList.contains('dark') || 
                        bodyElement.classList.contains('dark') ||
                        htmlElement.getAttribute('data-theme') === 'dark';
      
      console.log('[ProjectFinder] Dark mode detected:', this.isDarkMode);
    },

    /**
     * Observe theme changes
     */
    observeThemeChanges() {
      const observer = new MutationObserver(() => {
        const wasDark = this.isDarkMode;
        this.detectDarkMode();
        
        // Update map theme if it changed
        if (wasDark !== this.isDarkMode && this.map) {
          console.log('[ProjectFinder] Theme changed to:', this.isDarkMode ? 'dark' : 'light');
          this.updateMapTheme(this.isDarkMode);
        }
      });

      // Observe both html and body elements
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'data-theme']
      });

      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      });

      this.themeObserver = observer;
    },

    /**
     * Update map theme
     */
    updateMapTheme(isDark) {
      if (!this.map) return;

      this.map.setOptions({
        styles: isDark ? DARK_MAP_STYLES : []
      });
    },

    /**
     * Load projects from API
     */
    async loadProjects() {
      this.loadingProjects = true;
      this.error = null;

      try {
        const response = await axios.get('/api/projects');
        this.projects = response.data;
      } catch (error) {
        console.error('Error loading projects:', error);
        this.error = 'Failed to load projects';
        this.$toast.error('Failed to load projects');
      } finally {
        this.loadingProjects = false;
      }
    },

    /**
     * Load building types from API
     */
    async loadBuildingTypes() {
      try {
        const response = await axios.get('/api/building-types');
        
        // Handle both array and object with types property
        this.buildingTypes = Array.isArray(response.data) 
          ? response.data 
          : (response.data.types || []);
      } catch (error) {
        console.error('Error loading building types:', error);
        this.buildingTypes = [];
      }
    },

    /**
     * Handle project selection
     */
    async onProjectSelected(projectId) {
      if (!projectId) {
        this.properties = [];
        this.projectCompIds = [];
        this.clearMarkers();
        return;
      }

      // Cancel any pending requests
      this.cancelPendingRequests();

      // Create new AbortController for this request
      this.currentRequestController = new AbortController();

      this.loadingProperties = true;
      this.error = null;

      try {
        // Get the full project data including comps
        const projectResponse = await axios.get(`/api/project/${projectId}`, {
          signal: this.currentRequestController.signal
        });

        // Extract building IDs from comps
        this.projectCompIds = projectResponse.data.comps?.map(comp => comp.building_id || comp.id) || [];

        // Use the new endpoint: /api/project/{id}/properties to get all buildings from project sites
        const response = await axios.get(`/api/project/${projectId}/properties`, {
          signal: this.currentRequestController.signal
        });

        console.log('[ProjectFinder] Project properties response:', response.data);

        // Map the building data to properties format
        this.properties = response.data.map(building => {
          const property = {
            id: building.id,
            name: building.address_line_1 || building.name || 'Unknown Building',
            address: building.address_line_1 || building.name,
            city: building.city,
            state: building.state,
            location: `${building.city || ''}${building.city && building.state ? ', ' : ''}${building.state || ''}`,
            latitude: building.latitude,
            longitude: building.longitude,
            available_sf: building.available_square_footage,
            total_sf: building.total_square_footage,
            type: building.type?.type,
            image_url: building.image?.view_url,
            is_added: this.isInProject(building.id), // Check if already in project
            is_imported: building.is_imported || false,
            scraper_status: building.scraper_status,
            scraper_job_id: building.scraper_job_id,
            building_id: building.building_id,
            link: building.link,
            loading: false,
            // Store original building data
            building: building
          };
          
          // Debug log for imported buildings
          if (property.is_imported) {
            console.log('[ProjectFinder] Imported building from project:', {
              id: property.id,
              scraper_status: property.scraper_status,
              building_id: property.building_id
            });
          }
          
          return property;
        });

        this.updateMapMarkers();
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Request canceled:', error.message);
        } else {
          console.error('Error loading properties:', error);
          this.error = 'Failed to load properties for this project';
          this.$toast.error('Failed to load properties');
        }
      } finally {
        this.loadingProperties = false;
        this.currentRequestController = null;
      }
    },

    /**
     * Handle filter change (debounced)
     */
    onFilterChange() {
      // Debounce filter changes
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        // Auto-search if we have enough criteria
        if (this.filters.city || this.filters.state || this.filters.zip) {
          this.searchProperties();
        }
      }, 500);
    },

    /**
     * Search properties based on filters
     */
    async searchProperties() {
      // Cancel any pending requests
      this.cancelPendingRequests();

      // Create new AbortController for this request
      this.currentRequestController = new AbortController();

      this.loadingProperties = true;
      this.error = null;

      try {
        const response = await axios.get('/api/buildings/search', {
          params: this.filters,
          signal: this.currentRequestController.signal
        });

        console.log('[ProjectFinder] Search response:', response.data);

        // Map the building data to properties format
        this.properties = response.data.map(building => {
          const property = {
            id: building.id,
            name: building.address_line_1 || building.name || 'Unknown Building',
            address: building.address_line_1 || building.name,
            city: building.city,
            state: building.state,
            location: `${building.city || ''}${building.city && building.state ? ', ' : ''}${building.state || ''}`,
            latitude: building.latitude,
            longitude: building.longitude,
            available_sf: building.available_square_footage,
            total_sf: building.total_square_footage,
            type: building.type?.type,
            image_url: building.image_url,
            is_added: this.isInProject(building.id), // Check if already in project
            is_imported: building.is_imported || false,
            scraper_status: building.scraper_status,
            scraper_job_id: building.scraper_job_id,
            building_id: building.building_id,
            link: building.link,
            loading: false,
            building: building
          };
          
          // Debug log for imported buildings
          if (property.is_imported) {
            console.log('[ProjectFinder] Imported building:', {
              id: property.id,
              scraper_status: property.scraper_status,
              building_id: property.building_id
            });
          }
          
          return property;
        });

        this.updateMapMarkers();
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Request canceled:', error.message);
        } else {
          console.error('Error searching properties:', error);
          this.error = 'Failed to search properties';
          this.$toast.error('Failed to search properties');
        }
      } finally {
        this.loadingProperties = false;
        this.currentRequestController = null;
      }
    },

    /**
     * Reset all filters
     */
    resetFilters() {
      this.filters = {
        city: '',
        state: '',
        zip: '',
        building_type_id: null,
        min_sf: '',
        max_sf: '',
        sf_percentage: 10
      };
      this.properties = [];
      this.clearMarkers();
    },

    /**
     * Update map markers for properties
     */
    updateMapMarkers() {
      this.clearMarkers();

      if (!this.map || this.properties.length === 0) {
        return;
      }

      const bounds = new google.maps.LatLngBounds();
      let hasValidCoordinates = false;
      let propertiesWithCoords = 0;
      let propertiesWithoutCoords = 0;

      this.properties.forEach(property => {
        if (!property.latitude || !property.longitude) {
          propertiesWithoutCoords++;
          console.log('[ProjectFinder] Property missing coordinates:', {
            id: property.id,
            name: property.name,
            city: property.city,
            state: property.state,
            latitude: property.latitude,
            longitude: property.longitude
          });
          return;
        }

        propertiesWithCoords++;
        const position = {
          lat: parseFloat(property.latitude),
          lng: parseFloat(property.longitude)
        };

        // Create marker with default Google Maps red pin
        const marker = new google.maps.Marker({
          position,
          map: this.map,
          title: property.address
        });

        // Create info window with custom styling
        const infoWindow = new google.maps.InfoWindow({
          content: this.createInfoWindowContent(property),
          maxWidth: 300
        });

        // Add click listener
        marker.addListener('click', () => {
          this.closeAllInfoWindows();
          infoWindow.open(this.map, marker);
        });

        this.markers.push(marker);
        this.infoWindows.push(infoWindow);
        bounds.extend(position);
        hasValidCoordinates = true;
      });

      console.log('[ProjectFinder] Marker Summary:', {
        totalProperties: this.properties.length,
        withCoordinates: propertiesWithCoords,
        withoutCoordinates: propertiesWithoutCoords,
        markersCreated: this.markers.length
      });

      // Fit map to markers with padding
      if (hasValidCoordinates) {
        console.log('[ProjectFinder] Fitting map to', this.markers.length, 'markers');
        
        // Add padding so markers aren't at the edge
        this.map.fitBounds(bounds, {
          top: 50,
          right: 50,
          bottom: 50,
          left: 50
        });
        
        // If only one marker, zoom to a reasonable level
        if (this.markers.length === 1) {
          setTimeout(() => {
            this.map.setZoom(12);
          }, 100);
        }
      } else {
        console.warn('[ProjectFinder] No properties have valid coordinates!');
        if (this.$toast) {
          this.$toast.warning(`${propertiesWithoutCoords} of ${this.properties.length} properties are missing location coordinates and cannot be displayed on the map.`);
        }
      }
    },

    /**
     * Focus on a specific property on the map
     */
    focusProperty(property) {
      if (!property.latitude || !property.longitude) {
        return;
      }

      const position = {
        lat: parseFloat(property.latitude),
        lng: parseFloat(property.longitude)
      };

      // Center map on property
      this.map.setCenter(position);
      this.map.setZoom(15);

      // Find and open the marker's info window
      const markerIndex = this.properties.findIndex(p => p.id === property.id);
      if (markerIndex !== -1 && this.infoWindows[markerIndex]) {
        this.closeAllInfoWindows();
        this.infoWindows[markerIndex].open(this.map, this.markers[markerIndex]);
      }
    },

    /**
     * Create info window content for a property (matching RealAtlas format)
     */
    createInfoWindowContent(property) {
      // Get computed CSS variables from the document
      const rootStyles = getComputedStyle(document.documentElement);
      const cardBg = rootStyles.getPropertyValue('--card').trim() || '#ffffff';
      const borderColor = rootStyles.getPropertyValue('--border').trim() || '#e5e7eb';
      const foregroundColor = rootStyles.getPropertyValue('--foreground').trim() || '#000000';
      const mutedForeground = rootStyles.getPropertyValue('--muted-foreground').trim() || '#6b7280';
      
      // Build details line with available info
      const details = [];
      if (property.available_sf) {
        details.push(`${this.formatNumber(property.available_sf)} SF`);
      }
      if (property.total_sf) {
        details.push(`${this.formatNumber(property.total_sf)} SF`);
      }
      
      const detailsText = details.join(' | ');
      
      return `
        <div style="min-width: 200px; max-width: 280px;">
          <div style="padding: 0.75rem; background: ${cardBg}; border: 1px solid ${borderColor}; border-radius: 0.5rem; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);">
            <h3 style="font-size: 0.875rem; font-weight: 400; color: ${foregroundColor}; margin: 0 0 0.25rem 0; line-height: 1.4;">${property.address}</h3>
            ${property.city || property.state ? `
              <p style="font-size: 0.75rem; color: ${mutedForeground}; margin: 0 0 0.5rem 0; line-height: 1.3;">${[property.city, property.state].filter(Boolean).join(', ')}</p>
            ` : ''}
            ${detailsText ? `
              <p style="font-size: 0.75rem; color: ${mutedForeground}; margin: 0; line-height: 1.3;">${detailsText}</p>
            ` : ''}
          </div>
        </div>
      `;
    },

    /**
     * Close all info windows
     */
    closeAllInfoWindows() {
      this.infoWindows.forEach(infoWindow => infoWindow.close());
    },

    /**
     * Clear all markers from map
     */
    clearMarkers() {
      this.markers.forEach(marker => marker.setMap(null));
      this.markers = [];
      this.infoWindows = [];
    },

    /**
     * Cancel pending requests
     */
    cancelPendingRequests() {
      if (this.currentRequestController) {
        this.currentRequestController.abort();
        this.currentRequestController = null;
      }
    },

    /**
     * Handle grid action (add/remove/request/status/select-project)
     */
    handleGridAction(payload) {
      const { action, item } = payload;
      console.log('[ProjectFinder] Grid action:', action, item);
      
      if (action === 'add') {
        this.addToProject(item);
      } else if (action === 'remove') {
        this.removeFromProject(item);
      } else if (action === 'request') {
        this.requestBuildingDetails(item);
      } else if (action === 'status') {
        this.checkScraperStatus(item);
      } else if (action === 'select-project') {
        this.promptSelectProject();
      }
    },

    /**
     * Handle disabled action click - show toast message
     */
    handleDisabledActionClick(payload) {
      const { message } = payload;
      if (message && this.$toast) {
        this.$toast.info(message);
      }
    },

    /**
     * Prompt user to select a project
     */
    promptSelectProject() {
      this.$toast.info('Please select a project from the dropdown above to add properties');
      
      // Focus the project select dropdown
      this.$nextTick(() => {
        const projectSelect = this.$el.querySelector('.project-finder-page__filter-item:first-child');
        if (projectSelect) {
          projectSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },

    /**
     * Check if building is in project
     */
    isInProject(buildingId) {
      // Handle both regular and imported building IDs
      if (typeof buildingId === 'string' && buildingId.startsWith('imported_')) {
        // For imported buildings that haven't been converted yet
        return false;
      }
      
      const inProject = this.projectCompIds.includes(buildingId);
      console.log('[ProjectFinder] isInProject check:', buildingId, inProject, this.projectCompIds);
      return inProject;
    },

    /**
     * Add property to project
     */
    async addToProject(property) {
      console.log('[ProjectFinder] Adding property to project:', property.id);
      
      // Determine the actual building ID to use
      let buildingId = property.id;
      
      // If this is an imported building that has been converted, use the building_id
      if (property.is_imported && property.building_id) {
        buildingId = property.building_id;
      }
      
      // Check if it's an imported building that hasn't been converted yet
      if (property.is_imported && !property.building_id) {
        this.$toast.info('This building must be converted from imported data before adding to projects. Please request details first.');
        return;
      }
      
      // Use Vue.set to ensure reactivity
      this.$set(property, 'loading', true);

      try {
        const response = await axios.post(`/projects/${this.selectedProjectId}/comps`, {
          building_id: buildingId
        });

        console.log('[ProjectFinder] Property added successfully:', response.data);
        
        // Add to project comp IDs
        this.projectCompIds.push(buildingId);
        
        // Use Vue.set to ensure reactivity
        this.$set(property, 'is_added', true);
        
        if (this.$toast) {
          this.$toast.success(response.data.message || 'Property added to project');
        }
      } catch (error) {
        console.error('[ProjectFinder] Error adding property:', error);
        
        if (this.$toast) {
          if (error.response && error.response.status === 409) {
            this.$toast.warning('Property already added to project');
          } else {
            this.$toast.error(error.response?.data?.message || 'Failed to add property');
          }
        }
      } finally {
        this.$set(property, 'loading', false);
      }
    },

    /**
     * Remove property from project
     */
    async removeFromProject(property) {
      console.log('[ProjectFinder] Removing property from project:', property.id);
      
      this.$set(property, 'loading', true);

      try {
        // Determine the actual building ID
        let buildingId = property.id;
        if (property.is_imported && property.building_id) {
          buildingId = property.building_id;
        }
        
        // First, get the project to find the comp ID
        const projectResponse = await axios.get(`/projects/${this.selectedProjectId}`);
        const comp = projectResponse.data.comps?.find(c => c.building_id === buildingId);
        
        if (!comp) {
          throw new Error('Comp not found in project');
        }

        // Use the comp's pivot ID or comp ID
        const compId = comp.pivot?.id || comp.id;
        
        const response = await axios.delete(`/projects/${this.selectedProjectId}/comps/${compId}`);

        console.log('[ProjectFinder] Property removed successfully');
        
        // Remove from project comp IDs
        this.projectCompIds = this.projectCompIds.filter(id => id !== buildingId);
        
        this.$set(property, 'is_added', false);
        
        if (this.$toast) {
          this.$toast.success(response.data.message || 'Property removed from project');
        }
      } catch (error) {
        console.error('[ProjectFinder] Error removing property:', error);
        
        if (this.$toast) {
          this.$toast.error(error.response?.data?.message || 'Failed to remove property');
        }
      } finally {
        this.$set(property, 'loading', false);
      }
    },

    /**
     * Request details for property
     */
    async requestBuildingDetails(property) {
      if (!property.is_imported) {
        return;
      }
      
      this.$set(property, 'loading', true);

      try {
        // Extract the imported building ID from the composite ID
        const importedBuildingId = property.id.toString().replace('imported_', '');
        
        // If this is a retry, show appropriate message
        const isRetry = property.scraper_status === 'failed';
        
        await axios.post(`/api/buildings/scraper/${importedBuildingId}/request`, {
          project_id: this.selectedProjectId // Pass project context for auto-add
        });
        
        if (isRetry) {
          this.$toast.success('Scraper request has been requeued. The building will be automatically added to your project once ready.');
        } else {
          this.$toast.success('Scraper request has been queued. The building will be automatically added to your project once ready.');
        }
        
        // Clear any previous error and update status
        this.$set(property, 'scraper_status', 'queued');
        this.$set(property, 'scraper_error', null);
        
        // Optionally, start polling for status
        this.pollScraperStatus(property, importedBuildingId);
        
      } catch (error) {
        console.error('Error requesting building details:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to request building details');
      } finally {
        this.$set(property, 'loading', false);
      }
    },

    /**
     * Check status of property
     */
    async checkScraperStatus(property) {
      if (!property.is_imported) {
        return;
      }
      
      this.$set(property, 'loading', true);

      try {
        const importedBuildingId = property.id.toString().replace('imported_', '');
        
        const response = await axios.get(`/api/buildings/scraper/${importedBuildingId}/status`);
        
        const status = response.data.status;
        
        // Update local building data
        this.$set(property, 'scraper_status', status);
        this.$set(property, 'building_id', response.data.building_id);
        
        if (status === 'done') {
          this.$toast.success('Building has been created successfully and added to your project!');
          
          // Add to project comp IDs
          if (property.building_id) {
            this.projectCompIds.push(property.building_id);
          }
          
          // Reload project comps from server
          await this.reloadProjectComps();
        } else if (status === 'failed') {
          this.$toast.error(response.data.error || 'Failed to scrape building details');
        } else {
          this.$toast.info(`Current status: ${status}. Please check again in a moment.`);
        }
        
      } catch (error) {
        console.error('Error checking scraper status:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to check scraper status');
      } finally {
        this.$set(property, 'loading', false);
      }
    },

    /**
     * Poll scraper status
     */
    pollScraperStatus(property, importedBuildingId, attempts = 0) {
      const maxAttempts = 60; // Poll for up to 10 minutes (10s intervals)
      
      if (attempts >= maxAttempts) {
        return;
      }
      
      setTimeout(async () => {
        try {
          const response = await axios.get(`/api/buildings/scraper/${importedBuildingId}/status`);
          
          const status = response.data.status;
          this.$set(property, 'scraper_status', status);
          this.$set(property, 'building_id', response.data.building_id);
          
          if (status === 'done') {
            this.$toast.success('The building has been successfully created and added to your project!');
            
            // Add to project comp IDs for UI update
            if (property.building_id) {
              this.projectCompIds.push(property.building_id);
            }
            
            // Reload project comps from server
            await this.reloadProjectComps();
          } else if (status === 'failed') {
            this.$toast.error(response.data.error || 'Failed to scrape building details');
          } else {
            // Continue polling
            this.pollScraperStatus(property, importedBuildingId, attempts + 1);
          }
        } catch (error) {
          console.error('Error polling scraper status:', error);
        }
      }, 10000); // Poll every 10 seconds
    },

    /**
     * Reload project comps
     */
    async reloadProjectComps() {
      try {
        console.log('Reloading project comps for project:', this.selectedProjectId);
        const response = await axios.get(`/projects/${this.selectedProjectId}`);
        
        console.log('Project data received:', response.data);
        
        if (response.data && response.data.comps) {
          console.log('Updated project comps:', response.data.comps);
          // Update projectCompIds
          this.projectCompIds = response.data.comps.map(comp => comp.building_id || comp.id);
        }
      } catch (error) {
        console.error('Error reloading project comps:', error);
      }
    },

    /**
     * Request details for property (legacy method - keeping for compatibility)
     */
    async requestDetails(property) {
      return this.requestBuildingDetails(property);
    },

    /**
     * Check status of property (legacy method - keeping for compatibility)
     */
    async checkStatus(property) {
      return this.checkScraperStatus(property);
    },

    /**
     * Format number with commas
     */
    formatNumber(value) {
      if (!value) return '0';
      return parseInt(value).toLocaleString();
    }
  }
};
</script>
